import React, { forwardRef, memo, useCallback, useContext, useMemo } from 'react';

import { ListItem, ListItemText } from '@mui/material';
import { ArrowCheckIcon } from '@sharedCR/coreDesignSystem/icons';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';

import ContextMenuContext from './ContextMenuContext';
import { contextMenuItemStyles, disabledContextMenuItemStyles } from './ContextMenuItem.styles';

const ContextMenuItem = forwardRef(
  (
    {
      isDisabled = false,
      isSubMenuItem = false,
      isCloseOnClick = true,
      label,
      icon: IconComponent = null,
      iconSize,
      onClick,
      onClose = null,
      sx = null,
      ...restProps
    },
    ref,
  ) => {
    const { onClose: onMenuClose } = useContext(ContextMenuContext);
    const closeMenu = onClose ?? onMenuClose;

    // MUI v6 ListItem ignores `disabled`, so the disabled look and the click guard live here.
    const itemSx = useMemo(
      () => [
        contextMenuItemStyles,
        isDisabled && disabledContextMenuItemStyles,
        ..._.castArray(sx),
      ],
      [isDisabled, sx],
    );

    const onListItemClick = useCallback(
      e => {
        if (isSubMenuItem || isDisabled) {
          return;
        }

        if (onClick) {
          onClick(e);
        }

        if (isCloseOnClick && closeMenu) {
          closeMenu();
        }
      },
      [onClick, closeMenu, isSubMenuItem, isDisabled, isCloseOnClick],
    );

    return (
      <ListItem
        aria-disabled={isDisabled}
        ref={ref}
        sx={itemSx}
        onClick={onListItemClick}
        {...restProps}
      >
        {!!IconComponent && <IconComponent className="mr-5" size={iconSize} />}

        <ListItemText primary={label} />

        {isSubMenuItem && (
          <ArrowCheckIcon className="ml-5" direction={IconDirection.RIGHT} size={IconSize[20]} />
        )}
      </ListItem>
    );
  },
);

ContextMenuItem.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  iconSize: PropTypes.number,
  isCloseOnClick: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isSubMenuItem: PropTypes.bool,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.array]),
  onClick: PropTypes.func,
  onClose: PropTypes.func,
};

export default memo(ContextMenuItem);
